import { FC, ReactNode, useMemo, useCallback } from 'react';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import FeatureFlagsContext from './FeatureFlagsContext';

/**
 * The feature-flag map for the current user, with no fund in play.
 *
 * Mounted at the app root, ABOVE the router, because three things that read flags live above or
 * outside the fund routes: the site header, the app-root alerts, and the whole management area.
 * Before this existed, useFeature in any of those places fell back to the provider-less default
 * of false - so a flag could hide a management screen but never bring it back, and the switch on
 * /management/feature-flags did nothing for it.
 *
 * FeatureFlagsProvider stays nested inside FundProvider and overrides this one within the fund
 * routes, where the fund-scoped evaluation is the correct, narrower answer. Context nesting does
 * that on its own - there is nothing to coordinate between the two.
 *
 * Fund-shaped targeting is not available here (a flag InRollout to specific funds reads false),
 * which is right for a screen that belongs to no fund. Email and role targeting still work.
 */
const GlobalFeatureFlagsProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuthContext();

  // Anonymous visitors have no flags to fetch; the login and donation pages read none.
  const url = currentUser?.token ? 'feature-flags' : '';
  const { data, isLoading } = useGetAuthApi<Record<string, boolean>>(url);

  const flags = useMemo(() => data ?? {}, [data]);

  const isEnabled = useCallback(
    (featureKey: string) => flags[featureKey] === true,
    [flags],
  );

  const value = useMemo(
    () => ({ flags, isLoading, isEnabled }),
    [flags, isLoading, isEnabled],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default GlobalFeatureFlagsProvider;
